import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { logRetailInventoryWaste } from "@/lib/api/retail";
import type {
  InventoryClassificationSnapshot,
  RetailInventoryLevel,
} from "@/features/retail/types/datasets";

type KitchenWasteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level: RetailInventoryLevel | null;
  classification: InventoryClassificationSnapshot | null;
  productName: string | null;
  locationName: string | null;
  onSuccess?: () => void;
};

const DEFAULT_QUANTITY = "1";

const wasteReasons = ["spoilage", "overprep", "contamination", "quality", "other"] as const;

const KitchenWasteDialog = ({
  open,
  onOpenChange,
  level,
  classification,
  productName,
  locationName,
  onSuccess,
}: KitchenWasteDialogProps) => {
  const { t } = useTranslation(["common", "retail"]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState<(typeof wasteReasons)[number]>("spoilage");

  useEffect(() => {
    if (!open) {
      setQuantity(DEFAULT_QUANTITY);
      setNote("");
      setReason("spoilage");
    }
  }, [open]);

  const unitLabel = useMemo(() => {
    if (!classification) {
      return "ea";
    }
    return (
      classification.displayUnit?.abbreviation ??
      classification.unit?.abbreviation ??
      classification.unitId
    );
  }, [classification]);

  const mutation = useMutation({
    mutationFn: logRetailInventoryWaste,
    onSuccess: () => {
      toast({
        title: t("retail.inventory.kitchen.toast.waste", { defaultValue: "Waste logged." }),
      });
      queryClient.invalidateQueries({ queryKey: ["retail", "inventory"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast({
        title: t("retail.inventory.toast.error", { defaultValue: "Inventory update failed" }),
        description:
          error instanceof Error
            ? error.message
            : t("status.tryAgain", { defaultValue: "Please try again." }),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!level) {
      toast({
        title: t("retail.inventory.toast.error", { defaultValue: "Inventory update failed" }),
        description: t("retail.inventory.toast.missingLevelDescription", {
          defaultValue: "Refresh and try again.",
        }),
        variant: "destructive",
      });
      return;
    }
    const parsedQuantity = Number.parseFloat(quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      toast({
        title: t("retail.quickActions.receive.invalidQuantityTitle", {
          defaultValue: "Invalid quantity",
        }),
        description: t("retail.quickActions.receive.invalidQuantityDescription", {
          defaultValue: "Enter a positive number of units.",
        }),
        variant: "destructive",
      });
      return;
    }

    mutation.mutate({
      levelId: level.id,
      quantity: parsedQuantity,
      reason,
      note: note.trim() ? note.trim() : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("retail.inventory.kitchen.dialogs.waste.title", { defaultValue: "Log waste" })}
          </DialogTitle>
          <DialogDescription>
            {t("retail.inventory.kitchen.dialogs.waste.description", {
              defaultValue: "Capture waste reasons to tighten prep forecasts and menu availability.",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-5">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {productName ?? t("retail.inventory.table.notAvailable", { defaultValue: "-" })}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {locationName ??
                t("retail.receiving.unknownLocation", { defaultValue: "Unassigned location" })}
            </span>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-waste-quantity">
              {t("retail.inventory.kitchen.dialogs.waste.quantityLabel", { defaultValue: "Quantity discarded" })}
            </Label>
            <Input
              id="kitchen-waste-quantity"
              inputMode="decimal"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              placeholder="1"
              disabled={mutation.isPending}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.kitchen.dialogs.waste.quantityHint", {
                defaultValue: "Measured in {{unit}} units.",
                unit: unitLabel,
              })}
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-waste-reason">
              {t("retail.inventory.kitchen.dialogs.waste.reasonLabel", { defaultValue: "Reason" })}
            </Label>
            <Select
              value={reason}
              onValueChange={(value) => setReason(value as (typeof wasteReasons)[number])}
              disabled={mutation.isPending}
            >
              <SelectTrigger id="kitchen-waste-reason">
                <SelectValue
                  placeholder={t("retail.inventory.kitchen.dialogs.waste.reasonPlaceholder", {
                    defaultValue: "Select reason",
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {wasteReasons.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`retail.inventory.kitchen.dialogs.waste.reasons.${value}`, {
                      defaultValue: value,
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

  <div className="grid gap-2">
    <Label htmlFor="kitchen-waste-note">
      {t("retail.inventory.kitchen.dialogs.waste.noteLabel", { defaultValue: "Notes (optional)" })}
    </Label>
    <Textarea
      id="kitchen-waste-note"
      value={note}
      onChange={(event) => setNote(event.target.value)}
      rows={3}
      disabled={mutation.isPending}
    />
  </div>
</div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            {t("common.actions.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending}>
            {mutation.isPending
              ? t("retail.inventory.kitchen.dialogs.waste.submitPending", { defaultValue: "Saving…" })
              : t("retail.inventory.kitchen.dialogs.waste.submitLabel", { defaultValue: "Save waste log" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KitchenWasteDialog;
